import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { accountService } from "../../Api/Services/accountService";
import { InStoreUserRegister, SimplifiedUserInfo } from "../../Models/User";
import { CustomerType } from "../../Enums/CustomerType";
import {
  formatCnpj,
  formatCpf,
  formatPhoneNumber,
  unformat,
} from "../../Utils/DataFormatters";
import { Input } from "../Input/Input";
import { FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import SearchBar from "../SearchBar/SearchBar";
import PersonTypeToggle from "../PersonTypeToggle/PersonTypeToggle";

type CustomerStepProps = {
  selectedCustomerId: string | null;
  onSelectCustomer: (
    id: string | null,
    customerForm?: InStoreUserRegister | null,
    existingInfo?: {
      name: string;
      document: string;
      customerType?: CustomerType | null;
    } | null,
  ) => void;
};

const CustomerStep = forwardRef(function CustomerStep(
  { selectedCustomerId, onSelectCustomer }: CustomerStepProps,
  ref,
) {

  const [selectedExistingId, setSelectedExistingId] = useState<string | null>(
    selectedCustomerId ?? null,
  );
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [users, setUsers] = useState<SimplifiedUserInfo[]>([]);
  const [userSearch, setUserSearch] = useState("");
  const [isSearchingUsers, setIsSearchingUsers] = useState(true);
  const [initialLoadErrored, setInitialLoadErrored] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const [customerType, setCustomerType] = useState<CustomerType>(
    CustomerType.IndividualPerson,
  );
  const [form, setForm] = useState<InStoreUserRegister>({
    email: "",
    customerType: CustomerType.IndividualPerson,
    cpf: "",
    cnpj: "",
    phoneNumber: "",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setSelectedExistingId(selectedCustomerId ?? null);
  }, [selectedCustomerId]);

  const loadUsers = async (search?: string) => {
    const currentRequest = ++requestIdRef.current;
    setIsSearchingUsers(true);
    try {
      const data = await accountService.getUsers(1, 100, search);
      if (currentRequest === requestIdRef.current) {
        setUsers(data.items);
      }
    } catch {
      if (currentRequest === requestIdRef.current) {
        setUsers([]);
        if (!search) {
          setInitialLoadErrored(true);
        }
      }
    } finally {
      if (currentRequest === requestIdRef.current) {
        setIsSearchingUsers(false);
      }
    }
  };

  const normalizeSearch = (value: string) => {
    if (!value) {
      return "";
    }
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase();
  };

  const normalizeDigits = (value: string) => {
    return value ? value.replace(/\D/g, "") : "";
  };

  const triggerSearch = (value: string) => {
    setUserSearch(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      loadUsers(value);
    }, 250);
  };

  const filteredUsers = users.filter((u) => {
    if (!userSearch.trim()) {
      return true;
    }
    const term = normalizeSearch(userSearch);
    const termDigits = normalizeDigits(userSearch);
    const name = normalizeSearch(u.customerName ?? "");
    const email = normalizeSearch(u.email ?? "");
    const phone = normalizeSearch(u.phoneNumber ?? "");
    const phoneDigits = normalizeDigits(u.phoneNumber ?? "");
    const matchesText =
      name.includes(term) || email.includes(term) || phone.includes(term);
    const matchesDigits =
      termDigits.length > 0 && phoneDigits.includes(termDigits);
    return matchesText || matchesDigits;
  });

  const switchMode = (m: "existing" | "new") => {
    setMode(m);
    if (m === "existing") {
      setForm({
        email: "",
        customerType: CustomerType.IndividualPerson,
        cpf: "",
        cnpj: "",
        phoneNumber: "",
      });
      setSelectedExistingId(selectedCustomerId ?? null);
      onSelectCustomer(selectedCustomerId);
    } else {
      onSelectCustomer(null, form);
      setSelectedExistingId(null);
    }
  };

  const handleCustomerTypeChange = (type: CustomerType) => {
    setCustomerType(type);
    const updated: InStoreUserRegister = {
      email: form.email,
      customerType: type,
      cpf: type === CustomerType.IndividualPerson ? (form.cpf ?? "") : "",
      cnpj: type === CustomerType.LegalEntity ? (form.cnpj ?? "") : "",
      phoneNumber: form.phoneNumber,
    };
    setForm(updated);
    onSelectCustomer(null, updated);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "cpf" || name === "cnpj") {
      const digits = unformat(value);
      const updated = { ...form, [name]: digits };
      setForm(updated);
      onSelectCustomer(null, updated);
      return;
    }

    if (name === "phoneNumber") {
      const digits = unformat(value).slice(0, 11);
      const updated = { ...form, phoneNumber: digits };
      setForm(updated);
      onSelectCustomer(null, updated);
      return;
    }

    if (name === "email") {
      const updated = { ...form, email: value };
      setForm(updated);
      onSelectCustomer(null, updated);
    }
  };

  const validate = () => {
    if (mode === "existing") {
      // Customer is optional for in-store orders
      return true;
    }

    if (!form.email) {
      toast.error("Preencha o e-mail.");
      return false;
    }

    const phoneDigits = unformat(form.phoneNumber ?? "");
    if (phoneDigits.length !== 11) {
      toast.error("Informe um telefone celular válido com DDD.");
      return false;
    }

    if (form.customerType === CustomerType.IndividualPerson) {
      if (!form.cpf || unformat(form.cpf).length !== 11) {
        toast.error("Enter a valid CPF.");
        return false;
      }
    } else {
      if (!form.cnpj || unformat(form.cnpj).length !== 14) {
        toast.error("Informe um CNPJ válido.");
        return false;
      }
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  return (
    <div
      className={`flex flex-col bg-white border p-4 gap-5 flex-[1.6] min-h-[420px] rounded shadow`}
    >
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <FaUser className="text-primary-600" />
        <h4 className="text-md font-semibold">{"CLIENTE"}</h4>
      </div>

      <p className="text-xs text-gray-500 -mt-3">
        {"Cliente opcional. Para emitir NF-e, selecione ou cadastre um cliente."}
      </p>

      <div className="flex border-b">
        <button
          type="button"
          className={`flex-1 pb-2 text-sm font-semibold border-b-2 transition-colors ${
            mode === "existing"
              ? "border-primary-600 text-primary-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => switchMode("existing")}
        >
          {"Cliente já cadastrado"}
        </button>
        <button
          type="button"
          className={`flex-1 pb-2 text-sm font-semibold border-b-2 transition-colors ${
            mode === "new"
              ? "border-primary-600 text-primary-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => switchMode("new")}
        >
          {"Novo cliente"}
        </button>
      </div>

      {mode === "existing" && (
        <div className="flex flex-col gap-2">
          {!isSearchingUsers && (users.length > 0 || userSearch.length > 0 || initialLoadErrored) && (
            <SearchBar
              onSearch={(value) => {
                triggerSearch(value);
              }}
              value={userSearch}
              placeholder={"Buscar cliente por nome, e-mail ou telefone..."}
              className="w-full py-3"
              borderClass="border-gray-300"
              iconClass="text-primary-600"
            />
          )}
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-sm">
            {isSearchingUsers ? (
              <div className="p-3 space-y-2 animate-pulse">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {"Nenhum cliente encontrado."}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredUsers.map((u) => {
                  const userKeyStr = (u.customerId ?? u.id)?.toString();
                  if (!userKeyStr) {
                    return null;
                  }

                  const currentSelected = selectedExistingId === userKeyStr;
                  const handleSelect = () => {
                    setSelectedExistingId(userKeyStr);
                    onSelectCustomer(userKeyStr, undefined, {
                      name: u.customerName ?? "Cliente",
                      document:
                        u.customerType === CustomerType.IndividualPerson
                          ? formatCpf(u.customerDocument ?? "")
                          : formatCnpj(u.customerDocument ?? ""),
                      customerType: u.customerType,
                    });
                  };

                  return (
                    <li
                      key={userKeyStr}
                      className={`relative px-3 py-2 hover:bg-primary-50 cursor-pointer transition ${
                        currentSelected ? "bg-primary-50" : ""
                      }`}
                      onClick={handleSelect}
                    >
                      <span
                        aria-hidden
                        className={`absolute inset-y-0 left-0 w-1 ${
                          currentSelected ? "bg-primary-600" : "bg-transparent"
                        }`}
                      />
                      <div className="text-sm font-semibold text-gray-800">
                        {u.customerType === CustomerType.IndividualPerson
                          ? "Nome"
                          : "Razão Social"}
                        : {u.customerName ?? "Cliente"}
                      </div>
                      <div className="text-xs text-gray-600">
                        {"E-mail"}: {u.email} •{" "}
                        {"Telefone"}:{" "}
                        {formatPhoneNumber(u.phoneNumber)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {mode === "existing" && selectedExistingId && (
            <div className="text-xs text-gray-600 px-1">
              {"Cliente selecionado. Você pode avançar ou buscar outro cliente."}
            </div>
          )}
        </div>
      )}

      {mode === "new" && (
        <>
          {/* Tipo */}
          <PersonTypeToggle value={customerType} onChange={handleCustomerTypeChange} className="gap-4" />

          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {customerType === CustomerType.IndividualPerson ? (
              <Input
                label={"CPF"}
                name="cpf"
                placeholder="000.000.000-00"
                value={formatCpf(form.cpf ?? "")}
                onChange={handleFormChange}
                required
              />
            ) : (
              <Input
                label={"CNPJ"}
                name="cnpj"
                placeholder="00.000.000/0000-00"
                value={formatCnpj(form.cnpj ?? "")}
                onChange={handleFormChange}
                required
              />
            )}

            <Input
              label={"E-mail"}
              type="email"
              name="email"
              placeholder="cliente@email.com"
              value={form.email}
              onChange={handleFormChange}
              required
            />

            <Input
              label={"Telefone celular"}
              name="phoneNumber"
              type="tel"
              placeholder="(00) 00000-0000"
              value={formatPhoneNumber(form.phoneNumber ?? "")}
              onChange={handleFormChange}
              required
            />
          </div>
        </>
      )}
    </div>
  );
});

export default CustomerStep;
